import { styled } from '@styledComponents';
import { JoykitIcon } from '@components/JoykitIcon';
import { Button } from '@components/Button';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { Columns } from '@components/Columns';
import Modal from 'react-modal';
import { themeColor, themeMediaQuery, themeSpace, themeVariant } from '@utils/styledTheme.util';
import { BaseImage } from '@components/CloudImage';
import { animationTransition } from '@utils/animationTransition';
import { SelectDropdown } from '@components/SelectDropdown';
import { ButtonNoneStyled } from '@components/ButtonNoneStyled';

export const CARD_WIDTH_MOBILE = 100;
export const CARD_HEIGHT_MOBILE = 150;
export const CARD_OFFSET_MOBILE = 20;
export const GAP_SIZE_MOBILE = 15;
export const CARD_WIDTH_DESKTOP = 300;
export const CARD_HEIGHT_DESKTOP = 450;
export const CARD_OFFSET_DESKTOP = 80;
export const GAP_SIZE_DESKTOP = 70;

const isImageSquarish = (aspectRatio: number) => {
  return aspectRatio >= 0.9 && aspectRatio <= 1.1;
};

export const StyledModal = styled(Modal)`
  background-color: transparent;
  border: none;
  overflow: visible;
  margin: auto;
  width: 63%;

  ${themeMediaQuery('below_sm')} {
    width: 100%;
    height: 100%;
    overflow: auto;
  }
  ${themeMediaQuery('sm')} {
    width: 100%;
  }
  ${themeMediaQuery('md')} {
    width: 96%;
  }
  @media (min-width: 1400px) {
    width: 82%;
  }
  ${themeMediaQuery('lg')} {
    width: 80%;
  }
  ${themeMediaQuery('xl')} {
    width: 67%;
  }
  @media (min-width: 1920px) {
    width: 63%;
  }
`;

export const StyledColumns = styled(Columns)`
  background: white;
  min-height: 500px;
  border-radius: 12px;
  padding: 30px 10px;
  position: relative;
  margin-left: 0rem;
  margin-right: 0rem;
  ${themeMediaQuery('below_sm')} {
    margin: auto;
    padding: 20px 8px 20px 8px;
    border-radius: 0px;
  }
  ${themeMediaQuery('sm')} {
    max-width: 60rem;
  }
  ${themeMediaQuery('md')} {
    max-width: 70rem;
  }
  ${themeMediaQuery('xl')} {
    max-width: 75rem;
  }
`;

export const StyledCloseButton = styled(Button)`
  position: absolute;
  top: -30px;
  right: -10px;
  padding: 8px;
  background: none;
  border: none;
  width: 60px;
  height: 60px;
  background: #ffffff;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  :focus {
    outline: none;
  }
  ${themeMediaQuery('below_sm')} {
    top: -40px;
    right: -15px;
  }
`;

export const StyledInnerContainer = styled(Flex)`
  width: 100%;
  height: 100%;
`;

export const ECardImageContainer = styled.div<{ $isSmall: boolean }>`
  position: relative;
  margin-top: ${({ $isSmall }) => ($isSmall ? 0 : '34px')};
`;

export const ECardImageWrapper = styled.div<{ aspectRatio: number; $isSmall: boolean; showShadow?: boolean }>`
  position: relative;
  width: ${({ aspectRatio }) => (isImageSquarish(aspectRatio) ? '100%' : aspectRatio > 1 ? '100%' : 'unset')};
  height: ${({ aspectRatio }) => (isImageSquarish(aspectRatio) ? '100%' : aspectRatio < 1 ? '100%' : 'unset')};
  transition: ${animationTransition('transform')};
  box-shadow: ${({ showShadow }) =>
    showShadow ? '0px 9.1328px 24.6586px -4.5664px rgba(44, 41, 37, 0.25), 0px 6.39296px 14.6125px -7.30624px rgba(0, 0, 0, 0.3)' : '0px'};
  border-radius: 8px;
  ${themeMediaQuery('sm')} {
    transform: scale(1.001);
  }
`;

export const StyledFrame = styled.div`
  margin: ${themeSpace(2)} ${themeSpace(4)};
  padding: ${themeSpace(1)} 0px 0px 0px;
`;

export const ECardImage = styled(BaseImage)<{ $isLoaded: boolean; $isSmall: boolean; $isDesigns: boolean }>`
  height: 412px;
  visibility: ${({ $isLoaded }) => ($isLoaded ? 'visible' : 'hidden')};
  transition: ${animationTransition('box-shadow')};
  display: block;
  border-radius: ${({ $isDesigns }) => ($isDesigns ? '0px 0px' : '8px 8px')} 8px 8px;
  ${themeMediaQuery('below_sm')} {
    margin: auto;
    height: 204px;
  }
  ${themeMediaQuery('sm')} {
    height: 280px;
  }
  ${themeMediaQuery('md')} {
    height: 330px;
  }
  ${themeMediaQuery('lg')} {
    height: 380px;
  }
  ${themeMediaQuery('xl')} {
    height: 410px;
  }
`;

export const switchButtonWWWidth = 160;
export const switchButtonSTDWidth = 140;
export const switchButtonIWidth = 100;
export const switchButtonPWidth = 75;

const menuWidth = switchButtonWWWidth + switchButtonSTDWidth + switchButtonIWidth + switchButtonPWidth + 12;

const switchButtonWidthMobile = 148;
const switchButtonHeight = 35;
const switchPadding = 6;

export const ProductTypeMenu = styled.div<{
  isRightSelected: boolean;
  isInvitationSelected: boolean;
  isPrintSelected: boolean;
  hasPrint?: boolean;
  isMounted: boolean;
}>`
  position: relative;
  width: ${({ hasPrint }) => (hasPrint ? menuWidth : menuWidth - switchButtonPWidth)}px;
  background: ${themeColor('mono6')};
  border-radius: 100px;
  padding: ${switchPadding}px;
  margin-top: ${themeSpace(9)};
  ${themeMediaQuery('below_sm')} {
    display: none;
    margin-top: 40px;
    width: ${switchButtonWidthMobile * 2 + switchPadding * 2}px;
  }
  &:after {
    position: absolute;
    content: ' ';
    width: ${({ isRightSelected, isInvitationSelected, isPrintSelected }) =>
      isRightSelected
        ? switchButtonSTDWidth
        : isInvitationSelected
        ? switchButtonIWidth
        : isPrintSelected
        ? switchButtonPWidth
        : switchButtonWWWidth}px;
    height: ${switchButtonHeight}px;
    border-radius: ${switchButtonHeight / 2}px;
    background: ${themeColor('mono7')};
    box-shadow: 0px 1px 3px -1px rgba(51, 51, 51, 0.1);
    left: ${switchPadding}px;
    transform: translateX(
      ${({ isRightSelected, isInvitationSelected, isPrintSelected }) =>
        isRightSelected
          ? switchButtonWWWidth
          : isInvitationSelected
          ? switchButtonWWWidth + switchButtonSTDWidth
          : isPrintSelected
          ? switchButtonWWWidth + switchButtonSTDWidth + switchButtonIWidth
          : 0}px
    );
    visibility: ${({ isMounted }) => (isMounted ? 'visible' : 'hidden')};
    transition: ${animationTransition('transform')};
    top: ${switchPadding}px;
    z-index: 0;
    ${themeMediaQuery('below_sm')} {
      width: ${switchButtonWidthMobile}px;
      transform: translateX(
        ${({ isRightSelected, isInvitationSelected, isPrintSelected }) =>
          isRightSelected
            ? switchButtonWidthMobile
            : isInvitationSelected
            ? switchButtonWidthMobile * 2
            : isPrintSelected
            ? switchButtonWidthMobile * 3
            : 0}px
      );
    }
  }
`;

export const ProductTypeMenuItem = styled(ButtonNoneStyled)<{ $isActive?: boolean; width?: number }>`
  ${themeVariant('featureCopy17')};
  position: relative;
  width: ${({ width }) => (width ? width : switchButtonWWWidth)}px;
  justify-content: center;
  height: ${switchButtonHeight}px;
  line-height: ${switchButtonHeight}px;
  z-index: 1;
  color: ${({ $isActive }) => ($isActive ? themeColor('neutra6') : themeColor('neutra4'))};
  ${themeMediaQuery('below_sm')} {
    width: ${switchButtonWidthMobile}px;
  }
`;

export const StyledInput = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 7px 16px;
  width: 323px;
  background: #f7f7f7;
  border: 1px solid #d6d6d6;
  box-sizing: border-box;
  border-radius: 4px;
  margin: 10px 0px;
  ${themeMediaQuery('below_sm')} {
    max-width: 90vw;
  }
`;

export const StyledThemeTab = styled(Box)<{ isActive: boolean }>`
  border: ${({ isActive }) => (isActive ? '2px solid #5f2698' : '1px solid #BEBEBE')};
  box-sizing: border-box;
  border-radius: 4px;
  width: 58px;
  height: 78px;
  cursor: pointer;
  :hover {
    border: 2px solid #5f2698;
  }
`;

export const StyledCloseIcon = styled(JoykitIcon)`
  width: 15px;
  height: 15px;
  ${themeMediaQuery('below_sm')} {
    width: 12px;
    height: 12px;
  }
`;

export const StyledThemeJoykitIcon = styled(JoykitIcon)`
  width: 18px;
  height: 18px;
`;

export const StyledButton = styled(Button)`
  width: 100%;
  ${themeMediaQuery('below_sm')} {
    max-width: 350px;
    width: 100%;
  }
`;

export const ThemeTabWrapper = styled(Flex)``;

export const TextColumnWrapper = styled(Flex)`
  ${themeMediaQuery('below_sm')} {
    align-items: center;
  }
`;

export const RightFlex = styled(Flex)<{
  isDesgin: boolean;
}>`
  width: 100%;
  justify-content: flex-end;
  ${themeMediaQuery('below_sm')} {
    align-items: center;
  }
  ${themeMediaQuery('sm')} {
    margin-top: ${({ isDesgin }) => (isDesgin ? '0px' : '26px')};
  }
`;

export const LeftFlex = styled(Flex)`
  position: relative;
`;

export const CardTitleMobileWrapper = styled(Flex)`
  max-width: 80%;
  text-align: center;
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const CardTitleDesktopWrapper = styled(Flex)`
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const StartWithMobileButtonWrapper = styled(Flex)`
  width: 100%;
  position: fixed;
  bottom: 0;
  box-shadow: 0px -1px 0px #ebebeb;
  background: #ffffff;
  padding: 24px;
  justify-content: center;
  max-width: 35.75rem;
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const StartWithDesktopButtonWrapper = styled(Flex)`
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const CloseButtonMobileWrapper = styled(Flex)`
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const CloseButtonDesktopWrapper = styled(Flex)`
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const StyledBorder = styled.div`
  border: 1px solid #ebebeb;
`;

export const StyledBorderMobile = styled(StyledBorder)`
  display: none;
  ${themeMediaQuery('below_sm')} {
    display: block;
    margin-top: ${themeSpace(6)};
  }
`;

export const StyledVariantColumns = styled(Columns)`
  padding: 0px 2px;
  ${themeMediaQuery('below_sm')} {
    width: 104%;
  }
`;

export const StyledVariantColumn = styled(Columns.Column)`
  padding: ${themeSpace(0)} ${themeSpace(1)};
  cursor: pointer;
  ${themeMediaQuery('below_sm')} {
    display: inline-flex;
  }
`;

export const StyledStylesColumn = styled(StyledVariantColumn)`
  padding: ${themeSpace(1)} ${themeSpace(1)};
`;

export const VariantImageWrapper = styled.div<{ isSelected: boolean }>`
  border: ${({ isSelected }) => (isSelected ? '2px' : '1px')} solid
    ${({ isSelected }) => (isSelected ? themeColor('neutra6') : themeColor('mono3'))};
  border-radius: 4px;
  padding: ${themeSpace(1)} ${themeSpace(1)} ${themeSpace(0)};

  &:hover {
    border: 2px solid ${({ isSelected }) => (isSelected ? themeColor('neutra6') : themeColor('linkHover'))};
  }

  &:visited {
    border: 2px solid ${themeColor('linkPress')};
  }

  &:selected {
    border: 2px solid ${themeColor('neutra6')};
  }
`;

export const VariantLayoutWrapper = styled(ButtonNoneStyled)<{ isSelected: boolean; isHover: boolean }>`
  height: 61px;
  text-align: center;
  border-radius: 4px;
  white-space: initial;
  padding: ${themeSpace(5)};
  box-shadow: 0 0 0 ${({ isSelected, isHover }) => (isSelected || isHover ? '2px' : '1px')}
    ${({ isSelected, isHover }) => (isSelected ? themeColor('neutra6') : isHover ? themeColor('linkHover') : '#EBEBEB')};
  ${props => props.theme.mediaQueries.sm} {
    display: flex;
  }
  ${themeMediaQuery('sm')} {
    height: 85px;
  }
  ${themeMediaQuery('md')} {
    height: 61px;
  }
`;

export const VariantImage = styled(BaseImage)`
  width: 100%;
  border-radius: 4px;
`;

export const StyledPageIcon = styled(JoykitIcon)`
  width: 24px;
  height: 24px;
  margin-right: ${themeSpace(3)};
  vertical-align: sub;
  display: inline-block;
  ${themeMediaQuery('sm')} {
    margin-top: ${themeSpace(5)};
  }
  ${themeMediaQuery('md')} {
    margin-top: 0;
  }
`;

export const StyledSelectDropdownMobile = styled(SelectDropdown)<{ hasPrint?: boolean }>`
  display: none;
  ${themeMediaQuery('below_sm')} {
    display: block;
    li:last-child {
      display: ${({ hasPrint }) => (hasPrint ? 'block' : 'none')};
    }
  }
`;
